import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import PhotoDownloadDialog from "@/components/PhotoDownloadDialog";
import PhotoHealthCard from "@/components/PhotoHealthCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    ChevronRight,
    LogOut,
    MessageSquare,
    Plus,
    Search,
    Users,
    CheckCircle2,
    Clock,
    Download,
    Upload,
    RefreshCw,
    Pencil,
    ImageDown,
} from "lucide-react";
import AdminEditMemberDialog from "@/pages/AdminEditMemberDialog";
import { MemberCard } from "@/components/MemberCard";
import { WhatsAppComposeDialog } from "@/components/WhatsAppComposeDialog";

export default function AdminDashboard() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [positionFilter, setPositionFilter] = useState("all");
    const [tab, setTab] = useState("unfilled");
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", club_name: "", position: "", classification: "", ri_district: "" });
    const [sendingSid, setSendingSid] = useState("");
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [normalizing, setNormalizing] = useState(false);
    const bulkPhotoRef = useRef(null);
    const [editing, setEditing] = useState(null); // full member object being edited
    const [editingList, setEditingList] = useState([]); // ordered tokens from the filtered view
    const [editingIndex, setEditingIndex] = useState(-1);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const importRef = useRef(null);
    const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
    const [waMember, setWaMember] = useState(null);
    const openWhatsApp = (m) => setWaMember(m);
    const navigate = useNavigate();
    const { user, logout: doLogout } = useAuth();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/members");
            setMembers(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                navigate("/admin/login");
                return;
            }
            toast.error("Failed to load members");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const searched = !s
            ? members
            : members.filter(
                  (m) =>
                      m.name.toLowerCase().includes(s) ||
                      m.phone.toLowerCase().includes(s) ||
                      (m.club_name || "").toLowerCase().includes(s) ||
                      (m.position || "").toLowerCase().includes(s)
              );

        const byPosition = positionFilter === "all"
            ? searched
            : searched.filter((m) => (m.position || "").trim() === positionFilter);

        // Rotary position rank (higher = more senior). Unknown positions fall to alphabetical.
        const positionRank = (pos) => {
            const p = String(pos || "").toLowerCase();
            if (!p) return 0;
            if (p.includes("district governor") && p.includes("nominee")) return 90;
            if (p.includes("district governor") && p.includes("elect")) return 95;
            if (p.includes("past district governor") || p.includes("pdg") || p.includes("ipdg")) return 85;
            if (p.includes("district governor") || /\bdg\b/.test(p)) return 100;
            if (p.includes("vice governor")) return 78;
            if (p.includes("assistant governor") || /\bag\b/.test(p)) return 75;
            if (p.includes("zone chair") || p.includes("committee chair")) return 70;
            if (p.includes("president") && p.includes("elect")) return 58;
            if (p.includes("past president") || p.includes("ipp")) return 55;
            if (p.includes("president")) return 60;
            if (p.includes("secretary")) return 50;
            if (p.includes("treasurer")) return 48;
            if (p.includes("director") || p.includes("board")) return 45;
            if (p.includes("sergeant")) return 40;
            if (p.includes("chair")) return 35;
            if (p.includes("member") || p.includes("rotarian")) return 10;
            return 20;
        };

        const sorted = [...byPosition];
        sorted.sort((a, b) => {
            switch (sortBy) {
                case "position": {
                    const d = positionRank(b.position) - positionRank(a.position);
                    if (d !== 0) return d;
                    return (a.position || "").localeCompare(b.position || "") || a.name.localeCompare(b.name);
                }
                case "progress_desc":
                    return b.progress - a.progress || a.name.localeCompare(b.name);
                case "progress_asc":
                    return a.progress - b.progress || a.name.localeCompare(b.name);
                case "club":
                    return (a.club_name || "").localeCompare(b.club_name || "") || a.name.localeCompare(b.name);
                case "name":
                default:
                    return a.name.localeCompare(b.name);
            }
        });
        return sorted;
    }, [q, sortBy, positionFilter, members]);

    const positionOptions = useMemo(() => {
        const counts = new Map();
        members.forEach((m) => {
            const p = (m.position || "").trim();
            if (!p) return;
            counts.set(p, (counts.get(p) || 0) + 1);
        });
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    }, [members]);

    const filled = filtered.filter((m) => m.submitted);
    const unfilled = filtered.filter((m) => !m.submitted);
    const avgProgress = members.length
        ? Math.round(members.reduce((s, m) => s + m.progress, 0) / members.length)
        : 0;

    const sendReminder = async (m) => {
        setSendingSid(m.token);
        try {
            const { data } = await api.post(`/admin/members/${m.token}/reminder`);
            if (data.ok) toast.success(`Reminder sent to ${m.name}`, { description: data.detail });
            else toast(`Reminder queued`, { description: data.detail });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to send reminder");
        } finally {
            setSendingSid("");
        }
    };

    const addMember = async (e) => {
        e.preventDefault();
        // Strict 10-digit phone policy (iter 40) — matches backend validation.
        const digits = String(form.phone || "").replace(/\D+/g, "").slice(-10);
        if (digits.length !== 10) {
            toast.error("Phone must be exactly 10 digits (no country code, no spaces).");
            return;
        }
        try {
            const fd = new FormData();
            Object.entries({ ...form, phone: digits }).forEach(([k, v]) => fd.append(k, v));
            await api.post("/admin/members", fd);
            toast.success("Member added");
            setAddOpen(false);
            setForm({ name: "", phone: "", club_name: "", position: "", classification: "", ri_district: "" });
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(" · ") : (d || "Failed to add member");
            toast.error(msg);
        }
    };

    const logout = async () => {
        await doLogout();
        navigate("/admin/login");
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await api.get("/admin/export", { responseType: "blob" });
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `members-${stamp}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Directory exported");
        } catch (err) {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const name = file.name.toLowerCase();
        if (!(name.endsWith(".csv") || name.endsWith(".xlsx") || name.endsWith(".xls"))) {
            toast.error("Please upload a .csv or .xlsx file");
            return;
        }
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/import", fd);
            toast.success(
                `Import complete: ${data.inserted} added, ${data.updated} updated, ${data.skipped} skipped`
            );
            if (data.errors?.length) {
                toast("Some rows had issues", { description: data.errors.slice(0, 3).join(" · ") });
            }
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    const handleSyncSheet = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/sync-sheet");
            toast.success(
                `Sync complete: ${data.inserted} imported from Sheet, ${data.skipped} skipped`
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sync failed. Is the sheet shared with the service account?");
        } finally {
            setSyncing(false);
        }
    };

    const handleBulkPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setBulkUploading(true);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/admin/photos/upload", fd);
            toast.success(`Uploaded ${data.uploaded} photo(s)`);
            if (data.errors?.length) {
                toast("Some files skipped", { description: data.errors.slice(0, 3).join(" · ") });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk upload failed");
        } finally {
            setBulkUploading(false);
        }
    };

    const handleNormalize = async () => {
        if (!window.confirm("Normalize will rename uploaded photos to Name_Phone.ext for each member and update the sheet. Continue?")) return;
        setNormalizing(true);
        try {
            const { data } = await api.post("/admin/photos/normalize");
            toast.success(`Renamed ${data.renamed} · skipped ${data.skipped} · missing ${data.missing}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Normalize failed");
        } finally {
            setNormalizing(false);
        }
    };

    const openEdit = async (m) => {
        // Snapshot the current filtered/sorted list order so nav sticks to it.
        const tokens = filtered.map((x) => x.token);
        const idx = tokens.indexOf(m.token);
        setEditingList(tokens);
        setEditingIndex(idx);
        await fetchFullMember(m.token);
    };

    const fetchFullMember = async (token) => {
        setLoadingEdit(true);
        try {
            const { data } = await api.get(`/admin/members/${token}`);
            setEditing(data);
        } catch (err) {
            toast.error("Could not load member");
        } finally {
            setLoadingEdit(false);
        }
    };

    const gotoIndex = async (newIdx) => {
        if (newIdx < 0 || newIdx >= editingList.length) return;
        setEditingIndex(newIdx);
        await fetchFullMember(editingList[newIdx]);
    };

    const closeEdit = () => {
        setEditing(null);
        setEditingList([]);
        setEditingIndex(-1);
    };

    const onEdited = (updated) => {
        // Merge updated summary fields into the local list so cards re-render
        setMembers((prev) =>
            prev.map((x) =>
                x.token === updated.token
                    ? {
                          ...x,
                          name: updated.name,
                          phone: updated.phone,
                          club_name: updated.club_name,
                          position: updated.position,
                          classification: updated.classification,
                          ri_district: updated.ri_district,
                          submitted: updated.submitted,
                          progress: updated.progress,
                      }
                    : x
            )
        );
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-dashboard">
            {/* Header */}
            <header className="sticky top-0 z-30 glass-panel">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                    <Link to="/admin" className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-lg bg-rotary-blue text-white">
                            <Users className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="label-eyebrow leading-none">
                                {user?.role === "super_admin" ? "Super Admin" : "District Admin"}
                            </p>
                            <p className="rotary-heading text-lg leading-tight text-rotary-ink">Member Directory</p>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        {user?.role === "super_admin" && (
                            <Link to="/admin/platform">
                                <Button
                                    variant="outline"
                                    className="rounded-full hidden sm:inline-flex"
                                    data-testid="admin-platform-btn"
                                    title="Platform console"
                                >
                                    <Users className="mr-1.5 h-4 w-4" />
                                    Platform
                                </Button>
                            </Link>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => setPhotoDialogOpen(true)}
                            className="rounded-full hidden sm:inline-flex"
                            data-testid="admin-download-photos-btn"
                            title="Download member photos with filters"
                        >
                            <ImageDown className="mr-1.5 h-4 w-4" />
                            Photos
                        </Button>
                        <Button
                            variant="outline"
                            onClick={logout}
                            className="rounded-full"
                            data-testid="admin-logout-btn"
                        >
                            <LogOut className="mr-1.5 h-4 w-4" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-6 py-8 space-y-8">
                {/* Stats bento */}
                <section className="grid gap-4 sm:grid-cols-3">
                    <StatCard
                        label="Total members"
                        value={members.length}
                        icon={<Users className="h-5 w-5" />}
                    />
                    <StatCard
                        label="Submitted"
                        value={members.filter((m) => m.submitted).length}
                        icon={<CheckCircle2 className="h-5 w-5" />}
                        accent
                    />
                    <StatCard
                        label="Average profile complete"
                        value={`${avgProgress}%`}
                        icon={<Clock className="h-5 w-5" />}
                    />
                </section>

                {/* Photo health */}
                <PhotoHealthCard />

                {/* Toolbar */}
                <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="relative w-full sm:max-w-sm">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search by name, phone, club or position…"
                                className="h-11 pl-9"
                                data-testid="admin-search-input"
                            />
                        </div>
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="h-11 w-full sm:w-56" data-testid="admin-sort-select">
                                <SelectValue placeholder="Sort by…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="name" data-testid="sort-opt-name">Sort · Name (A–Z)</SelectItem>
                                <SelectItem value="position" data-testid="sort-opt-position">Sort · Position (seniority)</SelectItem>
                                <SelectItem value="club" data-testid="sort-opt-club">Sort · Club</SelectItem>
                                <SelectItem value="progress_desc" data-testid="sort-opt-progress-desc">Sort · Progress (high → low)</SelectItem>
                                <SelectItem value="progress_asc" data-testid="sort-opt-progress-asc">Sort · Progress (low → high)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={positionFilter} onValueChange={setPositionFilter}>
                            <SelectTrigger className="h-11 w-full sm:w-52" data-testid="admin-position-filter">
                                <SelectValue placeholder="Filter by position…" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="all" data-testid="position-opt-all">All positions</SelectItem>
                                {positionOptions.map(([pos, count]) => (
                                    <SelectItem
                                        key={pos}
                                        value={pos}
                                        data-testid={`position-opt-${pos.replace(/\s+/g, "-").toLowerCase()}`}
                                    >
                                        {pos} ({count})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            ref={importRef}
                            type="file"
                            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleImportFile}
                            className="hidden"
                            data-testid="admin-import-file-input"
                        />
                        <Button
                            variant="outline"
                            onClick={handleSyncSheet}
                            disabled={syncing}
                            className="rounded-full"
                            data-testid="admin-sync-sheet-btn"
                            title="Pull new rows from the Google Sheet"
                        >
                            <RefreshCw className={`mr-1.5 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                            {syncing ? "Syncing…" : "Sync from Sheet"}
                        </Button>
                        <input
                            ref={bulkPhotoRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            multiple
                            onChange={handleBulkPhotos}
                            className="hidden"
                            data-testid="admin-bulk-photos-input"
                        />
                        <Button
                            variant="outline"
                            onClick={() => bulkPhotoRef.current?.click()}
                            disabled={bulkUploading}
                            className="rounded-full"
                            data-testid="admin-bulk-photos-btn"
                            title="Bulk-upload photos with any filenames"
                        >
                            <Upload className="mr-1.5 h-4 w-4" />
                            {bulkUploading ? "Uploading…" : "Bulk photos"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleNormalize}
                            disabled={normalizing}
                            className="rounded-full"
                            data-testid="admin-normalize-btn"
                            title="Rename uploaded files to Name_Phone.ext based on the Photo column in the Sheet"
                        >
                            <RefreshCw className={`mr-1.5 h-4 w-4 ${normalizing ? "animate-spin" : ""}`} />
                            {normalizing ? "Normalizing…" : "Normalize filenames"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => importRef.current?.click()}
                            disabled={importing}
                            className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                            data-testid="admin-import-btn"
                        >
                            <Upload className="mr-1.5 h-4 w-4" />
                            {importing ? "Importing…" : "Import CSV / XLSX"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleExport}
                            disabled={exporting}
                            className="rounded-full"
                            data-testid="admin-export-btn"
                        >
                            <Download className="mr-1.5 h-4 w-4" />
                            {exporting ? "Exporting…" : "Export XLSX"}
                        </Button>
                        <Dialog open={addOpen} onOpenChange={setAddOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    className="bg-rotary-blue text-white hover:bg-rotary-sky rounded-full"
                                    data-testid="admin-add-member-btn"
                                >
                                    <Plus className="mr-1.5 h-4 w-4" /> Add member
                                </Button>
                            </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="rotary-heading text-2xl">Add member</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={addMember} className="space-y-4">
                                <Field label="Name" data-testid="add-name">
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        required
                                        data-testid="add-name-input"
                                    />
                                </Field>
                                <Field label="Phone" data-testid="add-phone">
                                    <Input
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D+/g, "").slice(-10) })}
                                        required
                                        placeholder="9876543210"
                                        maxLength={10}
                                        inputMode="numeric"
                                        data-testid="add-phone-input"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">10 digits — no country code, no spaces.</p>
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Club name">
                                        <Input
                                            value={form.club_name}
                                            onChange={(e) => setForm({ ...form, club_name: e.target.value })}
                                            data-testid="add-club-input"
                                        />
                                    </Field>
                                    <Field label="Position">
                                        <Input
                                            value={form.position}
                                            onChange={(e) => setForm({ ...form, position: e.target.value })}
                                            data-testid="add-position-input"
                                        />
                                    </Field>
                                </div>
                                <Field label="Classification">
                                    <Input
                                        value={form.classification}
                                        onChange={(e) => setForm({ ...form, classification: e.target.value })}
                                        placeholder="e.g. Architect, Doctor"
                                        data-testid="add-classification-input"
                                    />
                                </Field>
                                <Field label="RI District">
                                    <Input
                                        value={form.ri_district}
                                        onChange={(e) => setForm({ ...form, ri_district: e.target.value })}
                                        placeholder="e.g. 3192"
                                        data-testid="add-ri-district-input"
                                    />
                                </Field>
                                <DialogFooter>
                                    <Button
                                        type="submit"
                                        className="bg-rotary-blue text-white hover:bg-rotary-sky rounded-full"
                                        data-testid="add-member-submit-btn"
                                    >
                                        Save member
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                </section>

                {/* Tabs */}
                <Tabs value={tab} onValueChange={setTab} className="w-full">
                    <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto">
                        <TabTrigger value="unfilled" active={tab === "unfilled"} count={unfilled.length}>
                            Unfilled
                        </TabTrigger>
                        <TabTrigger value="filled" active={tab === "filled"} count={filled.length}>
                            Filled
                        </TabTrigger>
                        <TabTrigger value="all" active={tab === "all"} count={filtered.length}>
                            All
                        </TabTrigger>
                    </TabsList>

                    <TabsContent value="unfilled" className="mt-6">
                        <MemberGrid
                            members={unfilled}
                            loading={loading}
                            onReminder={sendReminder}
                            onWhatsApp={openWhatsApp}
                            sendingSid={sendingSid}
                            showReminder
                            onEdit={openEdit}
                        />
                    </TabsContent>
                    <TabsContent value="filled" className="mt-6">
                        <MemberGrid
                            members={filled}
                            loading={loading}
                            onReminder={sendReminder}
                            onWhatsApp={openWhatsApp}
                            sendingSid={sendingSid}
                            onEdit={openEdit}
                        />
                    </TabsContent>
                    <TabsContent value="all" className="mt-6">
                        <MemberGrid
                            members={filtered}
                            loading={loading}
                            onReminder={sendReminder}
                            onWhatsApp={openWhatsApp}
                            sendingSid={sendingSid}
                            showReminder
                            onEdit={openEdit}
                        />
                    </TabsContent>
                </Tabs>
            </main>

            <WhatsAppComposeDialog
                open={!!waMember}
                onOpenChange={(o) => !o && setWaMember(null)}
                member={waMember}
                variant="legacy"
            />

            <AdminEditMemberDialog
                open={editing !== null}
                onOpenChange={(open) => { if (!open) closeEdit(); }}
                member={editing}
                position={editingIndex >= 0 ? { current: editingIndex + 1, total: editingList.length } : null}
                hasPrev={editingIndex > 0}
                hasNext={editingIndex >= 0 && editingIndex < editingList.length - 1}
                onPrev={() => gotoIndex(editingIndex - 1)}
                onNext={() => gotoIndex(editingIndex + 1)}
                onSaved={(data, advance) => {
                    onEdited(data);
                    if (advance && editingIndex < editingList.length - 1) {
                        gotoIndex(editingIndex + 1);
                    } else if (!advance) {
                        // Save without advancing — refresh the current member view
                        setEditing(data);
                    }
                }}
            />

            <PhotoDownloadDialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen} />
        </div>
    );
}

function TabTrigger({ value, active, count, children }) {
    return (
        <TabsTrigger
            value={value}
            data-testid={`admin-tab-${value}`}
            className={`rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-semibold uppercase tracking-wider data-[state=active]:border-rotary-blue data-[state=active]:text-rotary-blue data-[state=active]:shadow-none ${
                active ? "" : "text-muted-foreground"
            }`}
        >
            {children}
            <span
                className={`ml-2 rounded-full px-2 py-0.5 text-[11px] ${
                    active ? "bg-rotary-blue text-white" : "bg-muted text-muted-foreground"
                }`}
            >
                {count}
            </span>
        </TabsTrigger>
    );
}

function StatCard({ label, value, icon, accent }) {
    return (
        <div
            className={`rounded-xl border p-6 ${
                accent ? "bg-rotary-blue text-white border-transparent" : "bg-white"
            }`}
        >
            <div className="flex items-center justify-between">
                <span className={`label-eyebrow ${accent ? "text-white/70" : ""}`}>{label}</span>
                <span
                    className={`grid h-8 w-8 place-items-center rounded-full ${
                        accent ? "bg-white/10 text-rotary-gold" : "bg-rotary-blue/10 text-rotary-blue"
                    }`}
                >
                    {icon}
                </span>
            </div>
            <p className={`mt-3 rotary-heading text-4xl ${accent ? "text-white" : "text-rotary-ink"}`}>
                {value}
            </p>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="label-eyebrow">{label}</Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function MemberGrid({ members, loading, onReminder, sendingSid, showReminder, onEdit, onWhatsApp }) {
    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-8 text-sm text-muted-foreground" data-testid="member-grid-loading">
                Loading members…
            </div>
        );
    }
    if (!members.length) {
        return (
            <div className="rounded-xl border bg-white p-8 text-sm text-muted-foreground" data-testid="member-grid-empty">
                No members in this group.
            </div>
        );
    }
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="member-grid">
            {members.map((m) => (
                <MemberCard
                    key={m.token}
                    m={m}
                    onReminder={onReminder}
                    onWhatsApp={onWhatsApp}
                    sending={sendingSid === m.token}
                    showReminder={showReminder}
                    onEdit={onEdit}
                />
            ))}
        </div>
    );
}

// MemberCard has been extracted to /app/frontend/src/components/MemberCard.jsx
